export const availableQuestionTypes = [
  "household_member",
  "encrypted_text",
  "date",
  "us_state",
  "array",
  "upload",
  "email",
  "money",
  "phone_number",
  "date_range",
  "multiple",
  "text",
  "boolean",
  "formatted",
  "address",
  "fax_number",
  "select",
] as const;

export type AvailableQuestionType = typeof availableQuestionTypes[number];

type QuestionTypeSpec = {
  description: string;
  requiredDataFields: string[];
  optionalDataFields: string[];
  supportsConditionals: boolean;
  supportsChildren: boolean;
  supportsArrayChildren: boolean;
};

const typePrefixes = availableQuestionTypes.map((type) => `${type}_`);
const typePostfixes = availableQuestionTypes.map((type) => `_${type}`);

export const questionTypeSpecs: Record<AvailableQuestionType, QuestionTypeSpec> = {
  "household_member": {
    description: "Household member object renderer.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  "encrypted_text": {
    description: "Encrypted free-text value.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  date: {
    description: "Single date value.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  "us_state": {
    description: "US state selection.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  array: {
    description: "Repeatable nested group.",
    requiredDataFields: ["data.label", "data.children"],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: false,
    supportsArrayChildren: true,
  },
  upload: {
    description: "Document upload question.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  email: {
    description: "Email input.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  money: {
    description: "Currency input.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  "phone_number": {
    description: "Phone number input.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  "date_range": {
    description: "Date range input with optional present flag.",
    requiredDataFields: [],
    optionalDataFields: ["data.allow_present", "data.label"],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  multiple: {
    description: "Multi-select options.",
    requiredDataFields: ["data.options"],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  text: {
    description: "Text input with optional mask/placeholder.",
    requiredDataFields: [],
    optionalDataFields: ["data.placeholder", "data.mask"],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  boolean: {
    description: "Boolean yes/no question.",
    requiredDataFields: [],
    optionalDataFields: ["data.message", "data.custom.yes_text", "data.custom.no_text"],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  formatted: {
    description: "Read-only formatted markdown content.",
    requiredDataFields: ["data.content"],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  address: {
    description: "Address object input.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  "fax_number": {
    description: "Fax number input.",
    requiredDataFields: [],
    optionalDataFields: [],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
  select: {
    description: "Single-select options.",
    requiredDataFields: ["data.options"],
    optionalDataFields: ["data.layout"],
    supportsConditionals: true,
    supportsChildren: true,
    supportsArrayChildren: false,
  },
};

export const guessQuestionTypeByName = (name: string): string | null => {
  const prefixType = typePrefixes.find((prefix) => name.startsWith(prefix));
  if (prefixType) return prefixType.replace("_", "");
  const postfixType = typePostfixes.find((postfix) => name.endsWith(postfix));
  if (postfixType) return postfixType.replace("_", "");
  return null;
};

const toString = (value: unknown): string => {
  if (typeof value === "string") return value;
  return JSON.stringify(value);
};

export const validateQuestionByType = (
  question: Record<string, any>,
  questionType: string,
  questionString: string,
): string[] => {
  const errors: string[] = [];

  switch (questionType) {
    case "multiple":
      if (!Array.isArray(question.data?.options) || question.data.options.length === 0) {
        errors.push(`${questionString} should have at least one question.data.options`);
      }
      break;
    case "formatted":
      if (!question.data?.content) {
        errors.push(`${questionString} should have question.data.content set`);
      }
      break;
    case "select":
      if (!Array.isArray(question.data?.options) || question.data.options.length === 0) {
        errors.push(`${questionString} should have at least one question.data.options`);
      }
      break;
    case "array":
      if (question.data) {
        if (!question.data.label) {
          errors.push(`${questionString} should have question.data.label`);
        }

        if (!Array.isArray(question.data.children) || question.data.children.length === 0) {
          errors.push(`${questionString} should have at least one question.data.children`);
        }
      } else {
        errors.push(`${questionString} should have question.data set`);
      }
      break;
    default:
      break;
  }

  const typeSpec = questionTypeSpecs[questionType as AvailableQuestionType];
  if (typeSpec) {
    return errors;
  }

  errors.push(`${questionString} has unsupported type ${toString(questionType)}`);

  return errors;
};
